import { Tooltip } from 'react-tooltip'

import ChainToken from '@src/components/ChainToken'
import CopyToClipboard from '@src/components/CopyToClipboard'
import { NATIVE_TOKEN_ADDRESS } from '@src/configs/chainInfo'
import { LiquiditySource } from '@src/enums'
import { useWalletConnections } from '@src/hooks/useWalletConnections'
import { FeatherOrderStatus } from '@src/pages/Exchange/components/FeatherOrderFlow/FeatherOrderStatus'
import useExchangeBalances from '@src/pages/Exchange/hooks/useExchangeBalances'
import { IExchangeData } from '@src/types/exchange'
import { IFee } from '@src/types/fee'
import { Address } from '@src/types/token'
import { formatExchangeAmountWithCommas } from '@src/utils/exchangeUtils'
import { formatExchangePrice } from '@src/utils/formatExchangePrice'
import { formatNumber } from '@src/utils/formatFee'
import { truncateAddress } from '@src/utils/truncateAddress'
import { useConfigStore } from '@src/zustand_stores'

interface IFeatherOrderDetailsProps {
    sellData: IExchangeData
    buyData: IExchangeData
    fees?: IFee[]
    destinationAddress?: Address
    orderId?: string
    depositTxHash?: string
    destinationTxHash?: string
    status?: FeatherOrderStatus
}

export default function FeatherOrderDetails({
    sellData,
    buyData,
    fees = [],
    destinationAddress,
    orderId,
    depositTxHash,
    destinationTxHash,
    status,
}: IFeatherOrderDetailsProps) {
    const availableNetworks = useConfigStore(config => config.availableNetworks)
    const { evmWalletAddress } = useWalletConnections()

    const { wrappedNativeBalance } = useExchangeBalances()

    const isNativeToken = sellData.token?.address === NATIVE_TOKEN_ADDRESS[sellData.chainId]
    const wrappedSymbol = isNativeToken ? `W${sellData.token?.symbol}` : ''

    // Get estimated processing time text based on status
    const getProcessingTimeText = () => {
        if (!status) return '15-30 minutes after deposit'

        switch (status) {
            case FeatherOrderStatus.WAITING_FOR_DEPOSIT:
                return '15-30 minutes after deposit'
            case FeatherOrderStatus.DEPOSIT_DETECTED:
                return '15-30 minutes'
            case FeatherOrderStatus.PROCESSING:
                return '10-15 minutes'
            case FeatherOrderStatus.SENDING_TO_DESTINATION:
                return '5-10 minutes'
            case FeatherOrderStatus.COMPLETED:
                return 'Completed'
            case FeatherOrderStatus.FAILED:
                return 'Failed'
            case FeatherOrderStatus.EXPIRED:
                return 'Expired'
            default:
                return '15-30 minutes after deposit'
        }
    }

    return (
        <>
            {/* You Pay Section */}
            <div className='space-y-2'>
                <p className='text-gray-500'>You Pay</p>
                <div className='flex items-center justify-between rounded-xl bg-[#1A1B1E] p-4 text-base font-medium'>
                    <div className='flex items-center gap-2'>
                        <img
                            src={sellData.token?.logoURI}
                            className='h-[20px] w-[20px] rounded-full'
                            onError={e => {
                                e.currentTarget.src = '/assets/token/unknown.png'
                            }}
                        />
                        <span className='max-w-[180px] truncate'>
                            {formatExchangeAmountWithCommas(sellData)} {sellData.token?.symbol}
                        </span>
                    </div>
                    <div className='flex items-center gap-2'>
                        <img
                            src={availableNetworks[sellData.chainId]?.icon}
                            className='h-[20px] w-[20px] rounded-full'
                        />
                        <span className='max-w-[130px] truncate'>{availableNetworks[sellData.chainId]?.name}</span>
                    </div>
                </div>

                {isNativeToken && (
                    <div className='mt-2 flex flex-col justify-between gap-2 md:flex-row md:items-center'>
                        <span className='text-mach-sub-text'>Current {wrappedSymbol} Balance</span>
                        <div className='flex items-center gap-1 text-sm'>
                            <div className='flex items-center gap-[6px]'>
                                <ChainToken
                                    tokenLogo={`/assets/token/w${sellData.token?.symbol?.toLowerCase()}.png`}
                                    tokenIconSize={14}
                                    chainIconSize={12}
                                />
                                <span className='max-w-[180px] truncate'>
                                    {wrappedNativeBalance && wrappedNativeBalance.formatWithCommas(18)}
                                </span>
                            </div>
                            <span>on</span>
                            <div className='flex items-center gap-[6px]'>
                                <img
                                    src={availableNetworks[sellData.chainId]?.icon}
                                    className='h-[14px] w-[14px] rounded-full'
                                />
                                <span>{availableNetworks[sellData.chainId]?.name}</span>
                            </div>
                        </div>
                    </div>
                )}
            </div>

            {/* You Receive Section */}
            <div className='space-y-2'>
                <p className='text-gray-500'>You Receive</p>
                <div className='flex items-center justify-between rounded-xl bg-[#1A1B1E] p-4 text-base font-medium'>
                    <div className='flex items-center gap-2'>
                        <img
                            src={buyData.token?.logoURI}
                            className='h-[20px] w-[20px] rounded-full'
                            onError={e => {
                                e.currentTarget.src = '/assets/token/unknown.png'
                            }}
                        />
                        <span className='max-w-[180px] truncate'>
                            {formatExchangeAmountWithCommas(buyData)} {buyData.token?.symbol}
                        </span>
                    </div>
                    <div className='flex items-center gap-2'>
                        <img
                            src={availableNetworks[buyData.chainId]?.icon}
                            className='h-[20px] w-[20px] rounded-full'
                        />
                        <span>{availableNetworks[buyData.chainId]?.name}</span>
                    </div>
                </div>
            </div>

            <div className='space-y-4 rounded-xl bg-[#1A1B1E]/50 p-4'>
                {/* Recipient */}
                {destinationAddress && (
                    <div className='flex flex-col justify-between gap-2 md:flex-row md:items-center'>
                        <span className='text-mach-sub-text'>Recipient Address</span>
                        <div className='flex items-center gap-[6px]'>
                            <CopyToClipboard
                                anchorId='dest-address'
                                textToCopy={destinationAddress || evmWalletAddress!}
                            />
                            <span className='text-sm'>{truncateAddress(destinationAddress || evmWalletAddress!)}</span>
                        </div>
                    </div>
                )}

                {/* Order ID */}
                {orderId && (
                    <div className='flex flex-col justify-between gap-2 md:flex-row md:items-center'>
                        <span className='text-mach-sub-text'>Order ID</span>
                        <div className='flex items-center gap-[6px]'>
                            <CopyToClipboard anchorId='order-id' textToCopy={orderId} />
                            <span className='text-sm'>{truncateAddress(orderId)}</span>
                        </div>
                    </div>
                )}

                {/* Deposit Transaction Hash */}
                {depositTxHash && (
                    <div className='flex flex-col justify-between gap-2 md:flex-row md:items-center'>
                        <span className='text-mach-sub-text'>Deposit Transaction</span>
                        <div className='flex items-center gap-[6px]'>
                            <CopyToClipboard anchorId='deposit-tx' textToCopy={depositTxHash} />
                            <span className='text-sm'>{truncateAddress(depositTxHash)}</span>
                        </div>
                    </div>
                )}

                {/* Destination Transaction Hash */}
                {destinationTxHash && (
                    <div className='flex flex-col justify-between gap-2 md:flex-row md:items-center'>
                        <span className='text-mach-sub-text'>Destination Transaction</span>
                        <div className='flex items-center gap-[6px]'>
                            <CopyToClipboard anchorId='dest-tx' textToCopy={destinationTxHash} />
                            <span className='text-sm'>{truncateAddress(destinationTxHash)}</span>
                        </div>
                    </div>
                )}

                {/* Exchange Type */}
                <div className='flex flex-col justify-between gap-2 md:flex-row md:items-center'>
                    <span className='text-mach-sub-text'>Exchange Type</span>
                    <div className='flex items-center gap-2'>
                        <span
                            className='w-fit cursor-help items-center rounded-md bg-blue-500/20 px-2 py-0.5 text-xs text-blue-400'
                            data-tooltip-id='feather-info'
                        >
                            Feather Order
                        </span>
                        <Tooltip
                            id='feather-info'
                            style={{ zIndex: 1000, maxWidth: '300px' }}
                            content='Feather is a non-custodial exchange service that enables cross-chain transactions without wrapping tokens.'
                        />
                    </div>
                </div>

                {/* Total Fees */}
                <div className='flex flex-col justify-between gap-2 md:flex-row md:items-center'>
                    <span className='text-mach-sub-text'>Total Fees</span>
                    {fees.reduce((res, cur) => (res += cur.value), 0) === 0 ? (
                        <span className='w-fit items-center rounded-md bg-mach-mach-green px-2 py-0.5 text-xs text-white'>
                            FREE
                        </span>
                    ) : (
                        <span className='text-sm'>
                            ${formatNumber(fees.reduce((res, cur) => (res += cur.value), 0))}
                        </span>
                    )}
                </div>

                {/* Rate */}
                <div className='flex flex-col justify-between gap-2 md:flex-row md:items-center'>
                    <span className='text-mach-sub-text'>Rate</span>
                    <span className='flex items-center text-sm'>
                        1&nbsp;
                        <span className='max-w-20 truncate'>{sellData.token?.symbol}</span>
                        &nbsp;=&nbsp;
                        {formatExchangePrice(sellData, buyData)}&nbsp;
                        <span className='max-w-20 truncate'>{buyData.token?.symbol}</span>
                    </span>
                </div>

                {/* Processing Time */}
                <div className='flex flex-col justify-between gap-2 md:flex-row md:items-center'>
                    <span className='text-mach-sub-text'>Estimated Processing Time</span>
                    <span className='text-sm'>{getProcessingTimeText()}</span>
                </div>
            </div>
        </>
    )
}
